import { forwardRef, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import moment from "moment-timezone";
import Avatar from "components/shared/Avatar";
import Checkbox from "components/shared/inputs/Checkbox";
import ShiftRow from "./ShiftRow";
import { useRequestCutoff } from "../../../../../Component/hooks/useRequestCutoff";
import { renderUserName } from "utils/helpers/user";
import { getUserId, toShift, type Shift, type ShiftPatch } from "./types";

interface UserShiftsProps {
    users?: any[];
    events: any[];
    loading?: boolean;
    value?: Shift[];
    onChange?: (value: Shift[]) => void;
}

const UserShifts = forwardRef<HTMLDivElement, UserShiftsProps>(({ users, events, loading, value = [], onChange }, ref) => {
    const { t } = useTranslation();
    const { formState: { errors, submitCount } } = useFormContext();
    const { isTooLate } = useRequestCutoff();
    const shiftErrors = errors.shifts as any;

    const [checkedAtSubmit, setCheckedAtSubmit] = useState<Record<string, number>>({});
    const canShowReasonError = (id: string) => submitCount > (checkedAtSubmit[id] ?? -1);

    const eventsByUser = useMemo(() => {
        const sorted = [...events].sort((a, b) => moment(a.startDate).valueOf() - moment(b.startDate).valueOf());
        return sorted.reduce<Record<string, any[]>>((total, event) => {
            (total[getUserId(event)] ||= []).push(event);
            return total;
        }, {});
    }, [events]);

    const isSelected = (event: any) => value.some((shift) => shift.id === event.id);

    const select = (selected: any[]) => {
        const added = selected.filter((event) => !isSelected(event) && !isTooLate(event.startDate)).map(toShift);
        setCheckedAtSubmit((current) => ({
            ...current,
            ...Object.fromEntries(added.map((shift) => [shift.id, submitCount])),
        }));
        onChange?.([...value, ...added]);
    };

    const deselect = (deselected: any[]) => {
        const ids = deselected.map((event) => event.id);
        onChange?.(value.filter((shift) => !ids.includes(shift.id)));
    };

    const toggle = (selected: any[], checked: boolean) => checked ? select(selected) : deselect(selected);

    const update = (id: string, patch: ShiftPatch) => {
        if ("reason" in patch) {
            setCheckedAtSubmit((current) => ({ ...current, [id]: -1 }));
        }
        onChange?.(value.map((shift) => shift.id === id ? { ...shift, ...patch } : shift));
    };

    if (!users?.length) {
        return (
            <p ref={ref} className="tw-px-4 tw-py-6 tw-text-sm tw-text-center tw-text-gray-500 tw-border tw-border-dashed tw-rounded-lg">
                {t("select-users-to-see-shifts")}
            </p>
        );
    }

    return (
        <div ref={ref} className="tw-flex tw-flex-col tw-gap-3">
            {users.map((user) => {
                const userEvents = eventsByUser[user.id] || [];
                const selectableCount = userEvents.filter((event) => isSelected(event) || !isTooLate(event.startDate)).length;
                const selectedCount = userEvents.filter(isSelected).length;
                const allChecked = selectedCount > 0 && selectedCount === selectableCount;

                return (
                    <div key={user.id} className="tw-border tw-rounded-lg">
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-py-3 tw-border-b tw-bg-gray-50 tw-rounded-t-lg">
                            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold">
                                <Avatar user={user} size="xs" textStyles="text-xs" />
                                {renderUserName(user)}
                            </div>
                            {userEvents.length > 0 && (
                                <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500 tw-cursor-pointer">
                                    {t("select-all")}
                                    <Checkbox
                                        checked={allChecked ? true : selectedCount > 0 ? "indeterminate" : false}
                                        onCheckedChange={(checked) => toggle(userEvents, checked === true)}
                                    />
                                </label>
                            )}
                        </div>

                        {userEvents.length === 0 ? (
                            <p className="tw-px-4 tw-py-3 tw-text-sm tw-text-gray-500">
                                {loading ? t("loading") : t("no-shifts-in-period")}
                            </p>
                        ) : (
                            <div className="tw-flex tw-flex-col tw-divide-y">
                                {userEvents.map((event) => {
                                    const index = value.findIndex((shift) => shift.id === event.id);
                                    const { reason: reasonError, ...otherErrors } = shiftErrors?.[index] ?? {};

                                    return (
                                        <ShiftRow
                                            key={event.id}
                                            event={event}
                                            shift={value[index]}
                                            errors={canShowReasonError(event.id) ? { reason: reasonError, ...otherErrors } : otherErrors}
                                            onToggle={(checked) => toggle([event], checked)}
                                            onChange={(patch) => update(event.id, patch)}
                                        />
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
});

export default UserShifts;
